import { createAsyncThunk } from "@reduxjs/toolkit";
import { toastr } from "react-redux-toastr";
import { TryCatch } from "../../../services/TryCatch";
import { COMPANY_ABBR } from "../../../common/constants/company";
import IDartsGameIdType from "../../../common/interfaces/IDartsGameIdType";
import IDartsGameIdTypeDate from "../../../common/interfaces/IDartsGameIdTypeDate";
import IDartsScoringTimeline from "./Timelines/DartsScoringTimelines.types";
import { API_DARTS_FIRSTPLAYER, API_DARTS_SCORE_INIT, API_DARTS_THROW, API_DARTS_THROW_DOUBLEATTEMPT, API_DARTS_UNDO_LAST_ACTION, API_DARTS_GAME_STATUS } from "../../../services/api/api.urls";
import { RootState } from '../../../store';
import { DartsGameStatusPayload, IFirstPlayerPayload, IDartsGameScoring,
    IDartsGameScoringInit, IDartsThrow, IDoubleAttemptPayload,
    IDartsGameStatus, IFirstPlayerPayloadData, IThrowPayload, IThrowPayloadData
} from "./DartsScoring.types";
import { api } from "../../../services/api/api.service";

export const initDartsGameScoring = createAsyncThunk<IDartsGameScoringInit, IDartsGameIdType, { rejectValue: string }>(
    'dartsGameScoring/init',
    async (props, { rejectWithValue }) => {
        const result = await TryCatch(
            () => api.post(API_DARTS_SCORE_INIT, props).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to fetch data for current darts game.')
            return rejectWithValue(result.error);
        }
        return result.data;
    }
);

export const setDartsGameStatus = createAsyncThunk<IDartsGameScoring, DartsGameStatusPayload, { state: RootState, rejectValue: string }>(
    'dartsGameScoring/setDartsGameStatus',
    async (props, { rejectWithValue, getState }) => {
        const state = getState();
        const game = state.DartsScoring.game;
        const payload: IDartsGameStatus = {
            gameId: game.gameId,
            gameType: game.gameType,
            date: new Date(),
            status: props.status,
            playerId: props.playerId,
        };
        const result = await TryCatch(
            () => api.put(API_DARTS_GAME_STATUS, payload).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to set darts game status.')
            return rejectWithValue(result.error);
        }
        return result.data;
    }
);

export const addThrow = createAsyncThunk<IDartsThrow, IThrowPayload, { rejectValue: string }>(
    'currentGame/addThrow',
    async (props, { rejectWithValue }) => {
       const result = await TryCatch(
            () => api.post(API_DARTS_THROW, props, { withCredentials: true, timeout: 30000 }).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to throw.')
            return rejectWithValue(result.error);
        }
        return result.data;
    }
);

export const doThrow = createAsyncThunk<IDartsThrow, IThrowPayloadData, { state: RootState, rejectValue: string }>(
    'currentGame/doThrow',
    async (props, { rejectWithValue, getState }) => {
        const state = getState();
        const game = state.DartsScoring.game;
        const payload: IThrowPayload = {
            gameId: game.gameId,
            gameType: game.gameType,
            date: new Date(),
            playerId: state.DartsScoring.playerId ?? 0,
            ring: props.ring,
            score: props.score,
            sector: props.sector
        };
        const result = await TryCatch(
            () => api.post(API_DARTS_THROW, payload, { withCredentials: true, timeout: 30000 }).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to throw.')
            return rejectWithValue(result.error);
        }
        return result.data;
    }
);

export const setFirstPlayer = createAsyncThunk<IDartsScoringTimeline[], IFirstPlayerPayloadData, { state: RootState, rejectValue: string }>(
    'currentGame/setFirstPlayer',
    async (props, { rejectWithValue, getState }) => {
        const state = getState();
        const game = state.DartsScoring.game;
        const payload: IFirstPlayerPayload = {
            gameId: game.gameId,
            gameType: game.gameType,
            date: new Date(),
            playerId: props.playerId
        };
        const result = await TryCatch(
            () => api.post(API_DARTS_FIRSTPLAYER, payload).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to setting a first player.')
            return rejectWithValue(result.error);
        }
        return result.data;
    }
);

export const undoLastAction = createAsyncThunk<{ scoring: IDartsGameScoringInit; requestMeta: IDartsGameIdTypeDate }, void, { state: RootState, rejectValue: string }>(
    'currentClassicGame/undoThrow',
    async (props, { rejectWithValue, getState }) => {
        const state = getState();
        const game = state.DartsScoring.game;
        const payload: IDartsGameIdTypeDate = {
            gameId: game.gameId,
            gameType: game.gameType,
            date: new Date()
        };
        const result = await TryCatch(
            () => api.post(API_DARTS_UNDO_LAST_ACTION, payload).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to undo.')
            return rejectWithValue(result.error);
        }
        return {
            scoring: result.data,
            requestMeta: payload
        };
    }
);

export const doubleAttempt = createAsyncThunk<null | number, IDoubleAttemptPayload, { rejectValue: string }>(
    'currentClassicGame/doubleAttempt',
    async (payload, { rejectWithValue }) => {
        const result = await TryCatch(
            () => api.post(API_DARTS_THROW_DOUBLEATTEMPT, payload).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to double attempt.")
            return rejectWithValue(result.error);
        }
        return result.data;
    }
);
